import { useContext, useEffect, useRef } from 'react';
import { UserContext, UserContextPayload } from '../context/userContext';
import { vendorDataResponse } from '../models/axiosModel';
import { useRuleBasedInboxStore } from '../store/ruleBasedInboxStore';
import { useVoiceCallStore } from '../store/voiceCallStore';
import {
    getEndUserPhoneNumber,
    resolveInboundCallIds,
} from './voiceCallDetails';
import { getVendorField } from './whiteLabelling';

const END_CALL_PATH = 'api/whatsapp-voice-call/end-call';

const ACTIVE_CALL_LEAVE_MESSAGE =
    'A voice call is ongoing. If you leave or refresh, the call will be ended.';

const getThreadPhoneNumber = () => {
    const { callingPersonDetails } = useVoiceCallStore.getState();
    return callingPersonDetails?.thread_metadata?.find(
        (data) => data?.metadata_type_name?.toLowerCase() === 'phone_number'
    )?.metadata_value || null;
};

const buildEndCallUrl = (vendorData?: vendorDataResponse | null) => {
    const baseUrl = (getVendorField(vendorData ?? null, "backend api", process.env.REACT_APP_BASE_URL) || '').trim().replace(/\/$/, '');
    if (!baseUrl) return END_CALL_PATH;
    return `${baseUrl}/${END_CALL_PATH}`;
};

/**
 * Returns true when an inbound/outbound voice call is dialing, connecting, or connected.
 */
const hasActiveVoiceCall = () => {
    const { voiceCallId, isCalling, isCallConnected } = useVoiceCallStore.getState();
    return Boolean(voiceCallId && (isCalling || isCallConnected));
};

/**
 * Ends an active voice call during page unload/refresh.
 * Uses fetch keepalive so the request can complete after the document starts unloading.
 */
const endActiveVoiceCallOnUnload = (vendorData?: vendorDataResponse | null) => {
    if (!hasActiveVoiceCall()) return;

    const { voiceCallId, callingWabaDetails, callingPersonDetails, inboundCallExecutionData, voiceCallDetails } = useVoiceCallStore.getState();
    const { getWabaNameAndPhoneNumberBytIntegratedChannelId } = useRuleBasedInboxStore.getState();

    const { callId, phoneNumberId } = resolveInboundCallIds({
        inboundCallExecutionData,
        voiceCallId,
        callingPersonDetails,
        callingWabaDetails,
        voiceCallDetails,
        getWabaNameAndPhoneNumberBytIntegratedChannelId,
    });

    const phoneNumber = getEndUserPhoneNumber(voiceCallDetails, getThreadPhoneNumber()) || '';
    const resolvedCallId = callId || voiceCallId;
    const resolvedPhoneNumberId = phoneNumberId || callingWabaDetails?.phone_number_id || '';

    if (!resolvedCallId || !resolvedPhoneNumberId || !phoneNumber) {
        console.warn('[VoiceCall] Unable to end call on unload — missing call details', {
            resolvedCallId,
            resolvedPhoneNumberId,
            hasPhoneNumber: Boolean(phoneNumber),
        });
        return;
    }

    const payload = {
        call_id: resolvedCallId,
        phone_number_id: resolvedPhoneNumberId,
        phone_number: phoneNumber,
    };

    try {
        void fetch(buildEndCallUrl(vendorData), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
            credentials: 'include',
            keepalive: true,
        });
    } catch (err) {
        console.error('[VoiceCall] Failed to end call on unload:', err);
    }
};

/**
 * Warns before leaving/refreshing during an active call, then ends the call only if the user confirms.
 * Reads `vendorData` from UserContext so the end-call request hits the correct (white-labelled) backend.
 */
export const useEndVoiceCallOnUnload = () => {
    const { vendorData } = useContext(UserContext) as UserContextPayload;

    // Keep the latest vendorData in a ref so the unload listener (registered once) never reads a stale value.
    const vendorDataRef = useRef(vendorData);
    vendorDataRef.current = vendorData;

    useEffect(() => {
        let hasEndedCall = false;

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            if (!hasActiveVoiceCall()) return;

            // Triggers the browser's native leave confirmation.
            // Custom text is ignored by most modern browsers; they show a generic leave/reload prompt.
            event.preventDefault();
            event.returnValue = ACTIVE_CALL_LEAVE_MESSAGE;
            return ACTIVE_CALL_LEAVE_MESSAGE;
        };

        const handlePageHide = () => {
            // Fires only when the page actually unloads (user confirmed leave/refresh).
            if (hasEndedCall) return;
            hasEndedCall = true;
            endActiveVoiceCallOnUnload(vendorDataRef.current);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('pagehide', handlePageHide);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('pagehide', handlePageHide);
        };
    }, []);
};
